import { intersectionBy } from 'lodash';
import type Luminary from '@lumen5/luminary';
import type {
    AudioSourceData,
    IconSourceData,
    MediaSourceData,
    PlaybackRegion,
    SceneGroup,
} from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import type { LexicalEditor } from 'lexical';

import type {
    ClipPlan,
    ClipPlansResponse,
    IconAssetModel,
    TranscriptClipResponse,
    TranscriptWord,
} from 'js/src/client';
import {
    ApiService,
    AssetTypes,
    CreationSourceEnum,
    LanguageEnum,
    TaskResultMessageTypeEnum,
    Urls,
} from 'js/src/client';
import { isTemplateValid } from 'js/src/components/app/video-creator-hooks';
import { INFINITE_TOAST_DURATION } from 'js/src/components/common/Toast';
import { VIDEO_INIT_TYPE } from 'js/src/data/app';
import {
    getTranscriptsToSaveForClipAction,
    updateNavigation,
    uploadFileAction,
} from 'js/src/data/clip-creator/clip-creator-actions';
import {
    CAPTION_STYLE_NONE,
    CLIP_CREATOR_PREFERRED_CLIP_TEMPLATE,
    CLIP_CREATOR_PREVIEW_TEMPLATE,
    CLIP_CREATOR_TEMPLATE_OVERLAY_SCENE_DESIGN,
    ClipCreatorQueryParams,
    ClipMediaCrop,
    WAVEFORM_STYLE_NONE,
} from 'js/src/data/clip-creator/clip-creator-consts';
import { ClipCreatorError } from 'js/src/data/clip-creator/clip-creator-error';
import {
    assertAudioSourceDataHasVoiceover,
    assertSourceDataHasAudio,
    assertSourceDataHasVoiceover,
    buildAudioSourceData,
    chooseWaveformSceneDesign,
    createEmptyClipLuminaryInstance,
    findNonOverlappingPosition,
    getClipCreatorProjectId,
    getSceneGroupByIdOrThrow,
    getVoiceDrivenAssetSourceDataOrUndefinedFromLuminary,
    getVoiceDrivenSceneGroupOrThrowFromLuminary,
    getVoiceDrivenSceneGroupOrUndefinedFromLuminary,
    isEmptyClip,
    isSavedClip,
    retrieveTranscriptForAsset,
    setLuminaryTemplate,
    updateClipCreatorQueryParams,
} from 'js/src/data/clip-creator/clip-creator-utils';
import type { ClipIDType } from 'js/src/data/clip-creator/slices/clip-creator-clips-slice';
import {
    addClipAndSelectAddedClip,
    deleteAllClipVersions,
    deleteClipAndSelectNextClip,
    getAllClipIds,
    getClipById,
    getClipVersionById,
    getClipVersionIds,
    getFaceDetectionsForClipId,
    getSelectedClip,
    getSelectedClipId,
    ORIGINAL_VERSION_ID,
    selectClipVersion,
    setClipHasCuts,
    setClipPrompt,
    setClipTitle,
    setFaceDetectionsForClipId,
    setSelectedClipId,
    setVideoIdForClipId,
    updateClipVersionLuminary,
} from 'js/src/data/clip-creator/slices/clip-creator-clips-slice';
import {
    getAspectRatioSetting,
    getCaptionStyleSetting,
    getWaveformStyleSetting,
} from 'js/src/data/clip-creator/slices/clip-creator-planner-slice';
import { getCurrentTime } from 'js/src/data/clip-creator/slices/clip-creator-preview-slice';
import {
    convertBackgroundAssetToFlattenedAsset,
    flattenedAssetIsVideo,
    getMediaResultsORM,
    immutableSlideBackgroundModelIsImmutableImageBackgroundModel,
    immutableSlideBackgroundModelIsImmutableVideoBackgroundModel,
    isAudioSourceData,
    isFlattenedAssetMediaSourceDataCompatible,
    isImmutableAudioAsset,
    SEARCH_CONTEXT,
    slideBackgroundModelIsVideoBackgroundModel,
} from 'js/src/data/media';
import { getFocusedSceneId } from 'js/src/data/scenes';
import { setFocusedSceneInfo } from 'js/src/data/scenes/scenes-actions';
import {
    loadSceneDesignsForTemplate,
    loadTemplate,
    loadTemplates,
} from 'js/src/data/templates/templates-operations';
import {
    getTemplateBundle,
    getTemplateDefaultTalkingHeadSceneDesign,
    getTemplateIdsForClipCreator,
} from 'js/src/data/templates/templates-selectors';
import type { GetTranscriptWordsOptions } from 'js/src/data/transcript';
import {
    $findMatchInParents,
    $getFirstTranscriptWordShowing,
    $getLastTranscriptWordShowing,
    $getNextWordAtTime,
    $getSelectedNodes,
    $getTimeIndicatedBySelection,
    CLEAR_SELECTION_COMMAND,
    getEditorFromSceneGroupId,
    getOriginalTranscriptLanguage,
    getTranscriptWords,
    loadNewTranscriptOperation,
    syncTranscriptToLuminaryOperation,
    theseAreOverlapping,
    TypeOfTranscript,
} from 'js/src/data/transcript';
import { $isBaseOverlayElementNode } from 'js/src/data/transcript/nodes/BaseOverlayElementNode';
import type { getUploadedAssetByUuid } from 'js/src/data/uploads';
import { getAllSessionUploadedAssetUuids } from 'js/src/data/uploads';
import { isVideoLimitError } from 'js/src/hooks/video-limit-error';
import ClipCreatorEventEmitter, {
    CLIP_CREATOR_EVENTS,
} from 'js/src/libs/clip-creator-event-emitter';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS, ImmutableToJS, isNotUndefined, MEDIA_ZOOM } from 'js/src/libs/consts';
import type { CropState } from 'js/src/libs/crop-utils';
import { createContainCropState, createFillCropState } from 'js/src/libs/crop-utils';
import { pluralize } from 'js/src/libs/custom-utils';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import FocusAndScroll from 'js/src/libs/focus-and-scroll';
import Navigation from 'js/src/libs/navigation';
import type { RootState } from 'js/src/libs/RootState';
import uuid from 'js/src/libs/uuid';
import WebSocketClient from 'js/src/libs/websocket-client';
import {
    buildTalkingHeadSceneOperation,
    buildVoiceoverSceneOperation,
    createNewVoicedrivenSceneGroupOperation,
    createOverlayOperation,
    cutVoicedrivenSceneGroupOperation,
    deleteAllOverlaysFromSceneGroupOperation,
    fetchAndSetRecommendedSceneAttributesOperation,
    getOverlayScenesInSceneGroup,
    getVoiceDrivenAsset,
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenAssetSourceData,
    getVoiceDrivenBaseAssetEndTime,
    getVoiceDrivenBaseAssetSourceDuration,
    getVoiceDrivenBaseAssetStartTime,
    isSceneAnOverlay,
    saveRawLuminary,
    setAllSceneGroupsCaptionVisibilityOperation,
    setCapabilitiesOperation,
    setCaptionsSceneDesignOperation,
    setMediaCrop,
    translateTranscriptTimeToVideoTime,
    translateVideoTimeToTranscriptTime,
} from 'js/src/luminary';
import {
    deduplicateByUuid,
    fetchVideoAssetsFromBackend,
    getAssetsNotTranscoded,
    setBackgroundTranscodedMediaAssetsOperation,
} from 'js/src/luminary/background-transcoding-operations';
import { equalWithinEpsilon } from 'js/src/luminary/comparison-utils';
import { setIconSouceDataForSceneOperation } from 'js/src/luminary/icon-operations';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    getFaceDetectionsOperation,
    moveOtherOverlaysAndCreateOverlayOperation,
    setUpMultipleSpeakersOnAllScenesOperation,
} from 'js/src/luminary/luminary-operations';
import { isVoiceoverSceneGroup } from 'js/src/luminary/luminary-selectors';
import { getNormalizedAssetBounds } from 'js/src/luminary/luminary-utils';
import {
    getVoicedrivenSceneDuration,
    getVoicedrivenSceneEndTime,
    getVoicedrivenScenesActiveAt,
    getVoicedrivenSceneStartTime,
    runVoicedrivenTranscriptBatchOperation,
    setVoicedrivenSceneDuration,
} from 'js/src/luminary/voicedriven-scenes';
import { isApiErrorWithProperty, isUploadError } from 'js/src/types';

const getTranscriptDataFromLuminaryOperation = createLuminaryOperation(
    ({ luminary, getState }, options: GetTranscriptWordsOptions = {}) => {
        const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(luminary);
        const sceneGroupId = sceneGroup.id;
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            throw new Error('No lexical editor found for scene group');
        }
        const transcriptWords = getTranscriptWords(editor, options);
        const transcriptLanguage = getOriginalTranscriptLanguage(getState(), sceneGroupId);
        return {
            transcriptWords,
            transcriptLanguage,
        };
    },
);

const getMediaDataFromLuminaryOperation = createLuminaryOperation(({ luminary }) => {
    const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(luminary);
    const sourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
    const sourceData = getVoiceDrivenAssetSourceData(sceneGroup);
    if (!sourceData) {
        throw new Error('Voice driven scene group has no source data');
    }
    const playbackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
    const playbackRegionEndTime = playbackRegions[playbackRegions.length - 1][1];
    if (playbackRegionEndTime > sourceDuration) {
        throw new Error('Clip playback region end time is greater than media duration');
    }
    let cutRegions: PlaybackRegion[] = playbackRegions.length
        ? VideoData.getCutRegions(playbackRegions, sourceDuration)
        : [];
    cutRegions = cutRegions.filter(([startTime, endTime]) => {
        const cutDuration = endTime - startTime;
        return cutDuration > 0;
    });
    return {
        sourceData,
        sourceDuration,
        playbackRegions,
        cutRegions,
    };
});

type CreateLuminaryOptions = Partial<Pick<BuildLuminaryOptions, 'templateId' | 'aspectRatio'>> & {
    name?: string;
};
const createClipLuminaryFromMainLuminaryOperation = createLuminaryOperation(
    async ({ luminary: sourceLuminary, call }, options: CreateLuminaryOptions = {}) => {
        const aspectRatio = options.aspectRatio ?? sourceLuminary.aspectRatio;
        const templateId = options.templateId ?? sourceLuminary.template.id;
        const { sourceData } = call(getMediaDataFromLuminaryOperation);
        const { transcriptWords, transcriptLanguage } = call(
            getTranscriptDataFromLuminaryOperation,
        );
        const targetLuminary = createEmptyClipLuminaryInstance(options.name);
        await call(
            buildLuminaryInstanceOperation,
            { templateId, sourceData, transcriptWords, transcriptLanguage, aspectRatio },
            { luminary: targetLuminary },
        );
        return targetLuminary;
    },
);

type BuildLuminaryOptions = {
    templateId: string;
    sourceData: MediaSourceData | AudioSourceData;
    transcriptWords: TranscriptWord[];
    transcriptLanguage: LanguageEnum;
    aspectRatio?: Luminary['aspectRatio'] | null;
};

/**
 * Given a luminary instance, this function does the following:
 * - Fetches the template bundle and sets the template
 * - Sets the aspect ratio
 * - Creates a new voicedriven scene group (deletes all other scene groups)
 * - Builds a talking head scene with the given source data
 * - Loads the transcript into the scene group
 * - Syncs the transcript to the luminary
 *
 * This function is intentionally kept generic because these operations have
 * to be done on the main luminary instance and on the clip luminary instances.
 */
export const buildLuminaryInstanceOperation = createLuminaryOperation(
    async (
        { luminary, call, getState },
        {
            sourceData,
            transcriptWords,
            transcriptLanguage,
            templateId,
            aspectRatio = null,
        }: BuildLuminaryOptions,
    ) => {
        call(setCapabilitiesOperation);
        const templateBundle = await call(fetchTemplateBundleOperation, templateId);
        setLuminaryTemplate(luminary, templateBundle!);

        const supportedAspectRatios = VideoData.getSupportedAspectRatios(templateBundle!);
        if (aspectRatio && supportedAspectRatios.includes(aspectRatio)) {
            call(setAspectRatioOperation, aspectRatio);
        }

        const isVoiceover = isAudioSourceData(sourceData);
        const sceneCategory = isVoiceover
            ? VideoData.CATEGORY_VOICEOVER
            : VideoData.CATEGORY_TALKING_HEAD;

        const { sceneId, sceneGroupId } = call(
            createNewVoicedrivenSceneGroupOperation,
            sceneCategory,
            0,
            {
                deleteOtherScenes: true,
                setPlaceholderAsset: false,
                hasAvatar: false,
            },
        );

        if (isVoiceover) {
            await call(buildVoiceoverSceneOperation, {
                sceneGroupId,
                audioAssetData: ImmutableFromJS(sourceData),
                avatarAssetData: null,
            });
        } else {
            await call(buildTalkingHeadSceneOperation, {
                sceneGroupId,
                mediaSourceData: sourceData,
                blueprintId: null,
            });
        }

        const showTranscript = false; // - no transcript panel on clip creator
        const editor = await call(
            loadNewTranscriptOperation,
            transcriptWords,
            transcriptLanguage,
            sceneGroupId,
            sceneId,
            showTranscript,
        );
        const editorRedux = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editorRedux) {
            throw new Error('Editor not found');
        }
        call(
            syncTranscriptToLuminaryOperation,
            editor,
            sceneGroupId,
            TypeOfTranscript.CLIP_CREATOR_MAIN,
        );

        if (!isVoiceover) {
            call(fetchAndSetRecommendedSceneAttributesOperation, {
                sceneId,
                autoCrop: false, // cropping is handled by face detection
                isGlobalVideo: false,
            });
        }
    },
);

const applySelectedClipPlaybackRegionsAsOverlaysOperation = createLuminaryOperation(
    ({ call, getState, luminary }) => {
        const selectedClipId = getSelectedClipId(getState());
        if (!selectedClipId) {
            // no selected clip.
            // perhaps we just deleted the last clip or something like that. Delete all the overlays
            // so there's none in the transcript.
            const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(luminary);
            call(deleteAllOverlaysFromSceneGroupOperation, sceneGroup.id);
            return;
        }
        call(applyClipPlaybackRegionsAsOverlaysOperation, selectedClipId);
    },
);

const applyClipPlaybackRegionsAsOverlaysOperation = createLuminaryOperation(
    ({ call, getState }, clipId: ClipIDType) => {
        let overlayRegions: [number, number][] = [];

        const clip = getClipById(getState(), clipId);
        if (clip && clip.hasCuts) {
            const clipLuminary = clip.luminary;
            const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(clipLuminary);
            const playbackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
            /**
             * Playback regions are in transcript time, so no need to translate them.
             * This loop is just to fix the type of the array i.e. remove readonly from playback regions.
             */
            overlayRegions = playbackRegions.map(([startTime, endTime]) => [startTime, endTime]);
        }

        call(applyOverlaysToVoiceDrivenSceneGroupOperation, overlayRegions);
    },
);

const applyOverlaysToVoiceDrivenSceneGroupOperation = createLuminaryOperation(
    ({ luminary, call }, overlayRegions: [number, number][]) => {
        const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(luminary);
        call(applyOverlaysToSceneGroupOperation, overlayRegions, sceneGroup);
    },
);

const applyOverlaysToSceneGroupOperation = createLuminaryOperation(
    ({ call }, overlayRegions: [number, number][], sceneGroup: SceneGroup) => {
        call(deleteAllOverlaysFromSceneGroupOperation, sceneGroup.id);
        if (overlayRegions.length === 0) {
            return;
        }
        for (const [startTime, endTime] of overlayRegions) {
            call(
                createOverlayOperation,
                sceneGroup.id,
                startTime,
                endTime,
                CLIP_CREATOR_TEMPLATE_OVERLAY_SCENE_DESIGN,
            );
        }
    },
);

const applyCutsToSceneGroupOperation = createLuminaryOperation(
    ({ luminary, call }, sceneGroup: SceneGroup, cutRegions: [number, number][]) => {
        const sourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        call(runVoicedrivenTranscriptBatchOperation, sceneGroup.id, () => {
            setVoicedrivenSceneDuration(sceneGroup.scenes[0], sourceDuration);
            const voiceDrivenAsset = getVoiceDrivenAsset(sceneGroup);
            if (voiceDrivenAsset) {
                voiceDrivenAsset.uncut(0, sourceDuration);
            }
            for (const [cutStartTime, cutEndTime] of cutRegions) {
                call(
                    cutVoicedrivenSceneGroupOperation,
                    sceneGroup.id,
                    cutStartTime,
                    cutEndTime,
                    true,
                );
            }
        });
    },
);

const applyCutsToVoiceDrivenSceneGroupOperation = createLuminaryOperation(
    (
        { luminary, call },
        cutRegions: [number, number][], // cuts are in transcript time
    ) => {
        const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(luminary);
        call(applyCutsToSceneGroupOperation, sceneGroup, cutRegions);
    },
);

export const applyPlaybackRegionsToSceneGroupOperation = createLuminaryOperation(
    ({ call }, sceneGroup: SceneGroup, playbackRegions: [number, number][]) => {
        const duration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        let cutRegions: PlaybackRegion[] = playbackRegions.length
            ? VideoData.getCutRegions(playbackRegions, duration)
            : [];
        cutRegions = cutRegions.filter(([startTime, endTime]) => {
            const cutDuration = endTime - startTime;
            return cutDuration > 0;
        });
        call(applyCutsToSceneGroupOperation, sceneGroup, cutRegions);
    },
);

export const applyPlaybackRegionsToVoiceDrivenSceneGroupOperation = createLuminaryOperation(
    ({ luminary, call }, playbackRegions: [number, number][]) => {
        const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(luminary);
        call(applyPlaybackRegionsToSceneGroupOperation, sceneGroup, playbackRegions);
    },
);

export const applyPlaybackRegionsToClipOperation = createLuminaryOperation(
    ({ call, getState }, clipId: number, playbackRegions: [number, number][]) => {
        const clip = getClipById(getState(), clipId);
        const luminary = clip.luminary;
        call(applyPlaybackRegionsToVoiceDrivenSceneGroupOperation, playbackRegions, {
            luminary,
        });
    },
);

const applyOverlaysAsPlaybackRegionsToClipOperation = createLuminaryOperation(
    ({ luminary: sourceLuminary, call, getState }, clipId: ClipIDType) => {
        const clip = getClipById(getState(), clipId);
        if (!clip) {
            throw new Error('Clip not found');
        }
        const targetLuminary = clip.luminary;

        const sourceSceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(sourceLuminary);
        const sourceSceneGroupStartTime = sourceSceneGroup.startTime;
        const sourceSceneGroupPlaybackRegions =
            getVoiceDrivenAssetPlaybackRegions(sourceSceneGroup);
        const sourceSceneGroupBaseAssetSourceDuration =
            getVoiceDrivenBaseAssetSourceDuration(sourceSceneGroup);
        const sourceSceneGroupOverlays = getOverlayScenesInSceneGroup(getState(), sourceSceneGroup);

        /**
         * We need to convert overlay times to transcript times.
         */
        const playbackRegions: [number, number][] = sourceSceneGroupOverlays.map(
            ({ startTime: overlayStartTime, endTime: overlayEndTime }) => [
                translateVideoTimeToTranscriptTime(
                    overlayStartTime,
                    sourceSceneGroupPlaybackRegions,
                    sourceSceneGroupBaseAssetSourceDuration,
                    sourceSceneGroupStartTime,
                ),
                translateVideoTimeToTranscriptTime(
                    overlayEndTime,
                    sourceSceneGroupPlaybackRegions,
                    sourceSceneGroupBaseAssetSourceDuration,
                    sourceSceneGroupStartTime,
                    {
                        includeTrailingCut: true,
                    },
                ),
            ],
        );

        call(applyPlaybackRegionsToVoiceDrivenSceneGroupOperation, playbackRegions, {
            luminary: targetLuminary,
        });
    },
);

const applyOverlaysAsPlaybackRegionsToSelectedClipOperation = createLuminaryOperation(
    ({ getState, call }) => {
        const clipId = getSelectedClipId(getState());
        if (!clipId) {
            throw new Error('No selected clip id found');
        }
        call(applyOverlaysAsPlaybackRegionsToClipOperation, clipId);
    },
);

const updateHasCutsForClipOperation = createLuminaryOperation(
    ({ luminary, getState, dispatch }, clipId: ClipIDType) => {
        const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(luminary);
        const overlays = getOverlayScenesInSceneGroup(getState(), sceneGroup);
        const hasOverlays = overlays.length > 0;
        dispatch(setClipHasCuts({ clipId, hasCuts: hasOverlays }));
    },
);

const updateHasCutsForSelectedClipOperation = createLuminaryOperation(({ call, getState }) => {
    const selectedClipId = getSelectedClipId(getState());
    if (!selectedClipId) {
        throw new Error('No selected clip id found');
    }
    call(updateHasCutsForClipOperation, selectedClipId);
});

export const handleFileSelectedOperation = createLuminaryOperation(
    async ({ call, dispatch }, files: File[]) => {
        if (files.length === 0) {
            throw new ClipCreatorError('No files selected', {
                toastMessage: 'No files selected. Please select a file to upload.',
                captureError: false, // obvious error, no need to send to sentry
            });
        }
        if (files.length > 1) {
            throw new ClipCreatorError('Multiple files selected', {
                toastMessage: 'Multiple files selected. Please select only one file.',
                captureError: false, // obvious error, no need to send to sentry
            });
        }

        let uploadedAsset: Awaited<ReturnType<typeof getUploadedAssetByUuid>>;
        try {
            uploadedAsset = await dispatch(uploadFileAction(files[0]));
        } catch (error) {
            throw new ClipCreatorError(error, {
                toastMessage: isUploadError(error)
                    ? error.message
                    : 'We ran into an issue while uploading your file. Please try again.',
                toastDuration: INFINITE_TOAST_DURATION,
                captureError: false, // upload errors are sent to sentry by `UploadMediaController.handleErrors`
            });
        }

        if (isImmutableAudioAsset(uploadedAsset)) {
            const audioAsset = uploadedAsset.toJS();
            if (!audioAsset.mp3_url) {
                throw new ClipCreatorError('Audio asset mp3_url not found', {
                    toastMessage:
                        'Something went wrong while processing your audio. Please try again.',
                    toastDuration: INFINITE_TOAST_DURATION,
                });
            }

            const audioSourceData = buildAudioSourceData(audioAsset);

            if (audioSourceData.uuid) {
                updateClipCreatorQueryParams({
                    [ClipCreatorQueryParams.mediaId]: audioSourceData.uuid,
                    [ClipCreatorQueryParams.mediaType]: AssetTypes.AUDIO,
                });
            }

            await call(handleBuildMainLuminaryOperation, audioSourceData);
            return;
        }
        if (immutableSlideBackgroundModelIsImmutableImageBackgroundModel(uploadedAsset)) {
            throw new ClipCreatorError('Image asset uploaded', {
                toastMessage: 'Your file was detected as an image. Please upload a video file.',
                toastDuration: INFINITE_TOAST_DURATION,
                captureError: false,
            });
        }
        if (!immutableSlideBackgroundModelIsImmutableVideoBackgroundModel(uploadedAsset)) {
            throw new ClipCreatorError('Unsupported asset type', {
                toastMessage:
                    'Your file was detected as an unsupported type. Please upload a video file.',
                toastDuration: INFINITE_TOAST_DURATION,
                captureError: false,
            });
        }

        let sourceData: MediaSourceData;
        try {
            // uploadedAsset type has been successfully narrowed down to ImmutableMap<VideoBackgroundModel>
            // it needs to be further narrowed down to MediaSourceData
            // if any of the following steps fail, it means something went wrong with the upload
            const flattenedAsset = convertBackgroundAssetToFlattenedAsset(uploadedAsset.toJS());
            if (!flattenedAssetIsVideo(flattenedAsset)) {
                throw new Error('Flattened asset is not a video');
            }
            if (!isFlattenedAssetMediaSourceDataCompatible(flattenedAsset)) {
                throw new Error('Flattened asset is not compatible with MediaSourceData');
            }
            sourceData = flattenedAsset;
        } catch (error) {
            throw new ClipCreatorError('Video data not found in background video', {
                toastMessage: 'Something went wrong while uploading your file. Please try again.',
                toastDuration: INFINITE_TOAST_DURATION,
            });
        }

        // add uploaded asset uuid to the url so we can skip the upload stage if user has to refresh the app for any reason
        updateClipCreatorQueryParams({
            [ClipCreatorQueryParams.mediaId]: sourceData.uuid,
            [ClipCreatorQueryParams.mediaType]: AssetTypes.VIDEO,
        });

        await call(handleBuildMainLuminaryOperation, sourceData);
        call(pollForBackgroundTranscodingOperation);
    },
);

export const handleVoiceDrivenMediaSelectedOperation = createLuminaryOperation(
    async ({ call }, mediaId: string, mediaType: AssetTypes = AssetTypes.VIDEO) => {
        updateClipCreatorQueryParams({
            [ClipCreatorQueryParams.mediaId]: mediaId,
            [ClipCreatorQueryParams.mediaType]: mediaType,
        });
        await call(handleLoadMediaOperation, mediaId, mediaType);
    },
);

export const handleLoadMediaOperation = createLuminaryOperation(
    async (
        { call },
        mediaId: string, // uuid of the uploaded asset
        assetType: AssetTypes,
    ) => {
        const isVoiceover = assetType === AssetTypes.AUDIO;
        let response: Awaited<ReturnType<typeof ApiService.bulkAssetSourceCreate>>;
        try {
            response = await ApiService.bulkAssetSourceCreate({
                body: {
                    asset_items: [
                        {
                            asset_type: assetType,
                            asset_uuid: mediaId,
                        },
                    ],
                },
            });
        } catch (e) {
            throw new ClipCreatorError(e, {
                toastMessage: `Error retrieving ${isVoiceover ? 'audio' : 'video'} asset`,
            });
        }

        const asset = response.data[0];

        if (isVoiceover) {
            const audioAsset = asset.audio;
            if (!audioAsset) {
                throw new ClipCreatorError('Audio asset not found', {
                    toastMessage: 'Error retrieving audio asset',
                });
            }
            const audioSourceData = buildAudioSourceData(audioAsset);
            await call(handleBuildMainLuminaryOperation, audioSourceData);
            return;
        }

        const background = asset.background;
        if (!background) {
            throw new ClipCreatorError('Background asset not found', {
                toastMessage: 'Error retrieving video asset',
            });
        }
        if (!slideBackgroundModelIsVideoBackgroundModel(background)) {
            throw new ClipCreatorError('Background asset is not a video', {
                toastMessage: 'Asset is not video',
            });
        }
        const flattenedAsset = convertBackgroundAssetToFlattenedAsset(background);
        if (!flattenedAssetIsVideo(flattenedAsset)) {
            throw new ClipCreatorError('Flattened asset is not a video', {
                toastMessage: 'Asset is not video',
            });
        }
        if (!isFlattenedAssetMediaSourceDataCompatible(flattenedAsset)) {
            throw new ClipCreatorError('Flattened asset is not compatible with MediaSourceData', {
                toastMessage: 'Asset is not video',
            });
        }

        const sourceData: MediaSourceData = flattenedAsset;
        await call(handleBuildMainLuminaryOperation, sourceData);
    },
);

const handleBuildMainLuminaryOperation = createLuminaryOperation(
    async (
        { call },
        sourceData: MediaSourceData | AudioSourceData,
        templateId = CLIP_CREATOR_PREVIEW_TEMPLATE,
    ) => {
        const isAudioOnly = isAudioSourceData(sourceData);

        if (isAudioOnly) {
            try {
                await assertAudioSourceDataHasVoiceover(sourceData);
            } catch (error) {
                throw new ClipCreatorError(error, {
                    toastMessage:
                        'We could not detect a voiceover in the recording. Please try again with clearer speech.',
                    toastDuration: INFINITE_TOAST_DURATION,
                });
            }
        } else {
            try {
                assertSourceDataHasAudio(sourceData);
            } catch (error) {
                throw new ClipCreatorError(error, {
                    toastMessage:
                        'We could not detect audio in the uploaded file. Please make sure the file has audio.',
                    toastDuration: INFINITE_TOAST_DURATION,
                });
            }
            try {
                await assertSourceDataHasVoiceover(sourceData);
            } catch (error) {
                throw new ClipCreatorError(error, {
                    toastMessage:
                        'We could not detect a voiceover in the uploaded file. Please make sure the file has a voiceover.',
                    toastDuration: INFINITE_TOAST_DURATION,
                });
            }
        }

        let transcriptWords: TranscriptWord[] = [];
        let transcriptLanguage = LanguageEnum.XX;
        try {
            const assetId = sourceData.uuid;
            if (!assetId) {
                throw new Error('Asset does not have uuid');
            }
            ({ transcriptWords, transcriptLanguage } = await retrieveTranscriptForAsset(
                assetId,
                isAudioOnly,
            ));
        } catch (error) {
            throw new ClipCreatorError(error, {
                toastMessage: 'We could not retrieve the transcript. Please try again.',
                toastDuration: INFINITE_TOAST_DURATION,
            });
        }

        try {
            await call(buildLuminaryInstanceOperation, {
                sourceData,
                transcriptWords,
                transcriptLanguage,
                templateId,
            });
        } catch (error) {
            throw new ClipCreatorError(error, {
                toastMessage:
                    'We ran into an issue while setting up the clip creator. Please try again.',
                toastDuration: INFINITE_TOAST_DURATION,
            });
        }
    },
);

export const isMainLuminaryBuiltOperation = createLuminaryOperation(({ luminary }) => {
    return !!getVoiceDrivenAssetSourceDataOrUndefinedFromLuminary(luminary);
});

const pollForBackgroundTranscodingOperation = createLuminaryOperation(
    ({ luminary, getState, call }) => {
        window.setTimeout(async () => {
            let clipIds = getAllClipIds(getState());
            let assetItems = clipIds
                .map((clipId) => {
                    const clip = getClipById(getState(), clipId);
                    return getAssetsNotTranscoded(clip.luminary);
                })
                .flat();

            // remove duplicates
            assetItems = deduplicateByUuid(assetItems);

            if (assetItems.length === 0) {
                // There is nothing to update!
                // return early and do not schedule a new poll.
                return;
            }

            const sourceDataMap = await fetchVideoAssetsFromBackend(assetItems);

            // re-fetch all the clipIds from redux - the user may have created a new clip
            // while the request was in flight
            clipIds = getAllClipIds(getState());

            // update the clip luminaries with the newly transcoded assets
            clipIds.forEach((clipId) => {
                const clipLuminary = getClipById(getState(), clipId).luminary;
                call(setBackgroundTranscodedMediaAssetsOperation, assetItems, sourceDataMap, {
                    luminary: clipLuminary,
                });
            });
            // also replace the main luminary with the newly transcoded assets
            const updatedAssets = call(
                setBackgroundTranscodedMediaAssetsOperation,
                assetItems,
                sourceDataMap,
            );

            if (updatedAssets.length > 0 && luminary.canvas) {
                // at least one asset was updated. Ask luminary to re-render the scene to avoid the preview going all white
                await luminary.renderAtTimeToCanvas(luminary.currentTime, luminary.canvas);
            }

            // re-trigger polling - if everything is updated, the next poll will return early
            call(pollForBackgroundTranscodingOperation);
        }, 30000);
    },
);

const applyFaceDetectionAndCroppingOperation = createLuminaryOperation(
    async ({ call, getState, dispatch }, clipId: ClipIDType) => {
        const clip = getClipById(getState(), clipId);
        if (!clip) {
            return;
        }

        const clipLuminary = clip.luminary;
        const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(clipLuminary);

        if (sceneGroup.scenes.length === 0) {
            return;
        }

        const firstScene = sceneGroup.scenes[0];
        const sceneId = firstScene.id;

        const existingFaceDetections = getFaceDetectionsForClipId(getState(), clipId);
        if (!existingFaceDetections) {
            try {
                const faceDetections = await call(getFaceDetectionsOperation, sceneId, {
                    luminary: clipLuminary,
                });
                dispatch(setFaceDetectionsForClipId({ clipId, faceDetections }));
            } catch (error) {
                throw new ClipCreatorError(error, {
                    toastMessage:
                        'We ran into an issue while generating face detections. Please try again.',
                });
            }
        }

        call(setUpMultipleSpeakersOnAllScenesOperation, { luminary: clipLuminary });
    },
);

const performNewClipOperation = createLuminaryOperation(
    async ({ call, dispatch, getState }, templateId?: string) => {
        if (!templateId) {
            templateId = await call(getLastUsedClipTemplateIdOperation);
        }
        const clipLuminary = await call(createClipLuminaryFromMainLuminaryOperation, {
            templateId,
        });
        dispatch(addClipAndSelectAddedClip({ luminary: clipLuminary })); // updates selected clip id to the new clip
        const clipId = getSelectedClipId(getState());
        if (!clipId) {
            throw new Error('No clip selected');
        }
        call(applyClipPlaybackRegionsAsOverlaysOperation, clipId);

        return clipId;
    },
);

export const handleDeleteClipOperation = createLuminaryOperation(
    ({ call, dispatch, getState }, clipId: ClipIDType) => {
        if (getAllClipIds(getState()).length === 1) {
            throw new ClipCreatorError('You must have at least one clip', {
                toastMessage: 'You must have at least one clip',
                captureError: false,
            });
        }
        try {
            call(performDeleteOperation, clipId);
            call(scrollToFirstOverlayInTranscriptOperation);
            dispatch(updateNavigation());
        } catch (error) {
            throw new ClipCreatorError(error, {
                toastMessage: 'We ran into an issue while deleting the clip. Please try again.',
            });
        }
    },
);

const performDeleteOperation = createLuminaryOperation(({ dispatch, call }, clipId: ClipIDType) => {
    dispatch(deleteClipAndSelectNextClip(clipId)); // updates selected clip id if necessary
    call(applySelectedClipPlaybackRegionsAsOverlaysOperation);
});

export const getClipPlaybackRegionsOperation = createLuminaryOperation(
    ({ getState }, clipId: ClipIDType) => {
        const clip = getClipById(getState(), clipId);
        if (!clip) {
            throw new Error('Clip not found');
        }
        const clipLuminary = clip.luminary;
        const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(clipLuminary);
        return getVoiceDrivenAssetPlaybackRegions(sceneGroup);
    },
);

export const handleSelectClipOperation = createLuminaryOperation(({ call }, clipId: ClipIDType) => {
    try {
        call(performSelectClipOperation, clipId);
        call(scrollToFirstOverlayInTranscriptOperation);
    } catch (error) {
        throw new ClipCreatorError(error, {
            toastMessage: 'We ran into an issue while selecting the clip. Please try again.',
        });
    }
});

const performSelectClipOperation = createLuminaryOperation(
    ({ call, dispatch }, clipId: ClipIDType) => {
        dispatch(setSelectedClipId(clipId));
        call(applyClipPlaybackRegionsAsOverlaysOperation, clipId);
    },
);

export const setFocusedSceneFromCurrentTimeOperation = createLuminaryOperation(
    ({ luminary, dispatch, getState }) => {
        const currentTime = getCurrentTime(getState());
        const activeScenes = luminary.getActiveScenesAt(currentTime);
        if (activeScenes.length === 0) {
            return;
        }
        dispatch(
            setFocusedSceneInfo({
                sceneGroupId: activeScenes[0].sceneGroup!.id,
                sceneId: activeScenes[0].id,
                subsceneIndex: 0,
            }),
        );
    },
);

const performAddSelectionToClipOperation = createLuminaryOperation(
    ({ call, dispatch, luminary, getState }, editor: LexicalEditor, sceneGroupId: string) => {
        const clip = getSelectedClip(getState());
        if (!clip) {
            throw new Error('No clip selected');
        }
        // TODO make operation from the following lines to avoid passing editor and sceneGroupId
        const sceneGroup = getSceneGroupByIdOrThrow(luminary, sceneGroupId);
        let overlayRegions = editor.getEditorState().read(() => {
            const selectionTime: [number, number][] = call(
                $getTimeBoundsFromSelectionOperation,
                sceneGroup,
            );
            return selectionTime;
        });
        const existingOverlays = call(getExistingOverlayTimesOperation, sceneGroupId);
        overlayRegions = overlayRegions.concat(existingOverlays);

        // merge any overlapping regions
        overlayRegions.sort((a, b) => a[0] - b[0]); // Sort by start time
        const mergedOverlayRegions: [number, number][] = [];
        for (const [startTime, endTime] of overlayRegions) {
            if (
                mergedOverlayRegions.length > 0 &&
                theseAreOverlapping(
                    mergedOverlayRegions[mergedOverlayRegions.length - 1][0],
                    mergedOverlayRegions[mergedOverlayRegions.length - 1][1],
                    startTime,
                    endTime,
                    { includingTouchingEndpoints: true },
                )
            ) {
                const lastRegion = mergedOverlayRegions.pop()!;
                mergedOverlayRegions.push([
                    Math.min(lastRegion[0], startTime),
                    Math.max(lastRegion[1], endTime),
                ]);
            } else {
                mergedOverlayRegions.push([startTime, endTime]);
            }
        }

        call(applyOverlaysToSceneGroupOperation, mergedOverlayRegions, sceneGroup);
        call(applyOverlaysAsPlaybackRegionsToSelectedClipOperation);
        call(defragClipCreatorOverlaysOperation);
        call(updateHasCutsForSelectedClipOperation);
        dispatch(updateNavigation());
        call(setFocusedSceneFromCurrentTimeOperation);

        editor.dispatchCommand(CLEAR_SELECTION_COMMAND, null);
    },
);

export const handleAddSelectionToClipOperation = createLuminaryOperation(
    (
        { call },
        editor: LexicalEditor, // TODO maybe we do not need this
        sceneGroupId: string, // TODO maybe we do not need this
    ) => {
        try {
            call(performAddSelectionToClipOperation, editor, sceneGroupId);
        } catch (error) {
            throw new ClipCreatorError(error, {
                toastMessage:
                    'We ran into an issue while adding the selection to the clip. Please try again.',
            });
        }
    },
);

export const handleOverlayDragHandleUpdatedForClipOperation = createLuminaryOperation(
    ({ call, dispatch, getState, luminary }) => {
        try {
            const focusedSceneId = getFocusedSceneId(getState());
            const scene = focusedSceneId ? luminary.getSceneById(focusedSceneId) : undefined;
            const focusedSceneGroupId = scene?.sceneGroup?.id ?? null;
            const sceneMidpoint = scene
                ? (getVoicedrivenSceneStartTime(scene) + getVoicedrivenSceneEndTime(scene)) / 2
                : null;
            call(applyOverlaysAsPlaybackRegionsToSelectedClipOperation);
            call(defragClipCreatorOverlaysOperation);
            call(updateHasCutsForSelectedClipOperation);
            dispatch(updateNavigation());

            // there is a chance that the scene has been merged with another scene.
            // set the focused scene to be the scene that represents the same time as
            // the old focused scene
            if (sceneMidpoint === null || !focusedSceneGroupId) {
                return;
            }
            const sceneGroup = luminary.getSceneGroupById(focusedSceneGroupId);
            if (!sceneGroup) {
                return;
            }
            const newScenes = getVoicedrivenScenesActiveAt(sceneGroup, sceneMidpoint);
            if (newScenes.length === 0) {
                return;
            }
            const newScene = newScenes[0];
            dispatch(
                setFocusedSceneInfo({
                    sceneGroupId: newScene.sceneGroup!.id,
                    sceneId: newScene.id,
                    subsceneIndex: 0,
                }),
            );
        } catch (error) {
            throw new ClipCreatorError(error, {
                toastMessage: 'We ran into an issue while updating the overlay. Please try again.',
            });
        }
    },
);

export const handleRemoveSelectionFromClipOperation = createLuminaryOperation(
    ({ call, getState, dispatch }, editor: LexicalEditor, sceneGroupId: string) => {
        const clip = getSelectedClip(getState());
        if (!clip) {
            throw new Error('No clip selected');
        }
        editor.getEditorState().read(() => {
            call($resizeOverlayFromSelectionOperation, { sceneGroupId });
        });
        call(applyOverlaysAsPlaybackRegionsToSelectedClipOperation);
        call(updateHasCutsForSelectedClipOperation);
        dispatch(updateNavigation());

        editor.dispatchCommand(CLEAR_SELECTION_COMMAND, null);
    },
);

export const handleSaveClipsOperation = createLuminaryOperation(async ({ getState, call }) => {
    const clipIds = getAllClipIds(getState());

    const saveRequests = clipIds.map((id) => {
        const clip = getClipById(getState(), id);
        const clipLuminary = clip.luminary;
        if (isEmptyClip(clip)) {
            return Promise.resolve(true);
        }
        if (isSavedClip(clip) && !clip.downloaded) {
            return call(performOverwriteClipOperation, id, {
                luminary: clipLuminary,
            });
        }
        return call(performSaveClipOperation, id, {
            luminary: clipLuminary,
        });
    });

    return Promise.allSettled(saveRequests);
});

export const handleSaveSingleClipOperation = createLuminaryOperation(
    async ({ getState, call }, clipId: ClipIDType) => {
        const clip = getClipById(getState(), clipId);
        const clipLuminary = clip.luminary;
        if (isSavedClip(clip)) {
            await call(performOverwriteClipOperation, clipId, {
                luminary: clipLuminary,
            });
        } else {
            await call(performSaveClipOperation, clipId, {
                luminary: clipLuminary,
            });
        }
        const { videoId } = getClipById(getState(), clipId);
        if (videoId === undefined) {
            throw new Error(`Clip ${clipId} could not be saved`);
        }
        return videoId;
    },
);

export const setAspectRatioOperation = createLuminaryOperation(
    ({ luminary }, aspectRatio: Luminary['aspectRatio']) => {
        luminary.aspectRatio = aspectRatio;
    },
);

const updateLuminaryAspectRatioOperation = createLuminaryOperation(
    (
        { luminary, call },
        aspectRatio: Luminary['aspectRatio'],
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        call(setAspectRatioOperation, aspectRatio);
        luminary.scenes.forEach((scene) => {
            call(fetchAndSetRecommendedSceneAttributesOperation, {
                sceneId: scene.id,
                autoCrop: false,
                systemTriggered,
                parentActionGroup,
                isGlobalVideo: false,
            });
        });
    },
);

const updateClipAspectRatioOperation = createLuminaryOperation(
    async (
        { getState, call },
        clipId: ClipIDType,
        aspectRatio: Luminary['aspectRatio'],
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        const versionIds = getClipVersionIds(getState(), clipId);
        for (const versionId of versionIds) {
            const { luminary } = getClipVersionById(getState(), clipId, versionId);
            if (!luminary) {
                throw new Error(`Luminary for clip version with id ${versionId} not found`);
            }
            call(
                updateLuminaryAspectRatioOperation,
                aspectRatio,
                systemTriggered,
                parentActionGroup,
                {
                    luminary,
                },
            );
            await call(applyFaceDetectionAndCroppingOperation, clipId);
        }
    },
);

export const handleChangeClipAspectRatioOperation = createLuminaryOperation(
    async (
        { call },
        clipId: ClipIDType,
        aspectRatio: Luminary['aspectRatio'],
        systemTriggered = false,
        parentActionGroup = uuid(),
    ) => {
        try {
            await call(
                updateClipAspectRatioOperation,
                clipId,
                aspectRatio,
                systemTriggered,
                parentActionGroup,
            );
        } catch (error) {
            throw new ClipCreatorError(error, {
                toastMessage: 'We could not change the aspect ratio. Please try again.',
            });
        }
    },
);

const updateClipTemplateOperation = createLuminaryOperation(
    async ({ getState, call, dispatch }, clipId: ClipIDType, templateId: string) => {
        dispatch(deleteAllClipVersions({ clipId }));
        const clip = getClipById(getState(), clipId);
        if (!clip) {
            throw new Error(`Clip with id ${clipId} not found`);
        }
        const oldLuminary = clip.luminary;
        if (!oldLuminary) {
            throw new Error(`Luminary for clip with id ${clipId} not found`);
        }
        const { cutRegions } = call(getMediaDataFromLuminaryOperation, {
            luminary: oldLuminary,
        });
        const newLuminary = await call(createClipLuminaryFromMainLuminaryOperation, {
            templateId,
        });
        call(applyCutsToVoiceDrivenSceneGroupOperation, cutRegions, { luminary: newLuminary });
        dispatch(
            updateClipVersionLuminary({
                clipId,
                versionId: ORIGINAL_VERSION_ID,
                luminary: newLuminary,
            }),
        );
        dispatch(selectClipVersion({ clipId, versionId: ORIGINAL_VERSION_ID }));
    },
);

export const handleChangeAllClipsTemplateOperation = createLuminaryOperation(
    async ({ call, getState }, templateId: string) => {
        try {
            await call(fetchTemplateBundleOperation, templateId);
        } catch (error) {
            throw new ClipCreatorError(error, {
                toastMessage: 'We could not retrieve the template data. Please try again.',
            });
        }

        try {
            const clipIds = getAllClipIds(getState());
            const promises = clipIds.map((clipId) =>
                call(updateClipTemplateOperation, clipId, templateId),
            );
            await Promise.all(promises);
        } catch (error) {
            throw new ClipCreatorError(error, {
                toastMessage:
                    'We could not update the template for all the clips. Please try again.',
            });
        }
    },
);

export const pauseClipPreviewOperation = createLuminaryOperation(({ getState }, clipId) => {
    const clip = getClipById(getState(), clipId);
    const clipLuminary = clip.luminary;
    if (clipLuminary.isPlaying()) {
        clipLuminary.pause();
    }
});

export const pauseAllClipPreviewsOperation = createLuminaryOperation(({ call, getState }) => {
    const clipIds = getAllClipIds(getState());
    for (const clipId of clipIds) {
        call(pauseClipPreviewOperation, clipId);
    }
});

export const defragClipCreatorOverlaysOperation = createLuminaryOperation(
    ({ luminary, getState, dispatch, call }) => {
        const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(luminary);
        call(runVoicedrivenTranscriptBatchOperation, sceneGroup.id, () => {
            for (let i = sceneGroup.scenes.length; i >= 0; i--) {
                const scene = sceneGroup.scenes[i];
                const nextScene = sceneGroup.scenes[i + 1];
                if (!scene || !nextScene) {
                    continue;
                }
                if (
                    isSceneAnOverlay(getState(), scene) &&
                    isSceneAnOverlay(getState(), nextScene)
                ) {
                    // both this scene and the next are overlays. We can collapse them into a single scene.
                    const duration =
                        getVoicedrivenSceneDuration(scene) + getVoicedrivenSceneDuration(nextScene);
                    setVoicedrivenSceneDuration(scene, duration);
                    sceneGroup.removeScene(nextScene.id);
                }
            }
        });
    },
);

export const $getTimeBoundsFromSelectionOperation = createLuminaryOperation(
    ({ call }, sceneGroup: SceneGroup) => {
        let canCreateOverlay = true;

        // cannot create overlay if overlay already exists for selected nodes
        const selectedNodes = $getSelectedNodes();
        for (const node of selectedNodes) {
            if ($findMatchInParents(node, $isBaseOverlayElementNode)) {
                canCreateOverlay = false;
                break;
            }
        }

        const selectionTime: [number, number][] = [];
        if (canCreateOverlay) {
            // create overlay from selection
            const { startTime, endTime } = call($getTimeIndicatedBySelection, sceneGroup.id);
            selectionTime.push([startTime, endTime]);
        }

        return selectionTime;
    },
);

const getExistingOverlayTimesOperation = createLuminaryOperation(
    ({ luminary, getState }, sceneGroupId) => {
        const sceneGroup = getSceneGroupByIdOrThrow(luminary, sceneGroupId);
        const sceneGroupPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const sceneGroupBaseAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const sceneGroupStartTime = sceneGroup.startTime;
        const translateToTranscriptTime = (
            time: number,
            { includeTrailingCut } = { includeTrailingCut: true },
        ) => {
            return translateVideoTimeToTranscriptTime(
                time,
                sceneGroupPlaybackRegions,
                sceneGroupBaseAssetSourceDuration,
                sceneGroupStartTime,
                {
                    includeTrailingCut,
                },
            );
        };
        return (
            getOverlayScenesInSceneGroup(getState(), sceneGroup)
                // get the existing overlay start/end times
                .map((overlay): [number, number] => {
                    const startTime = translateToTranscriptTime(
                        getVoicedrivenSceneStartTime(overlay),
                    );
                    const endTime = translateToTranscriptTime(getVoicedrivenSceneEndTime(overlay), {
                        includeTrailingCut: false,
                    });
                    return [startTime, endTime];
                })
        );
    },
);

export const $resizeOverlayFromSelectionOperation = createLuminaryOperation(
    (
        { luminary, call, getState },
        {
            sceneGroupId,
        }: {
            sceneGroupId: string;
        },
    ) => {
        // cannot remove from overlay if overlay does not exists for selected nodes
        const selectedNodes = $getSelectedNodes();
        for (const node of selectedNodes) {
            if (!$findMatchInParents(node, $isBaseOverlayElementNode)) {
                return;
            }
        }

        // remove selected nodes from overlay
        const { startTime: selectionStartTime, endTime: selectionEndTime } = call(
            $getTimeIndicatedBySelection,
            sceneGroupId,
        );
        const sceneGroup = getSceneGroupByIdOrThrow(luminary, sceneGroupId);
        const activeScenesAtSelectionStartTime = getVoicedrivenScenesActiveAt(
            sceneGroup,
            selectionStartTime + 1,
        ); // +1 to move time into the required scene
        const activeScenesAtSelectionEndTime = getVoicedrivenScenesActiveAt(
            sceneGroup,
            selectionEndTime - 1,
        ); // -1 to move time into the required scene
        const activeScene = intersectionBy(
            activeScenesAtSelectionStartTime,
            activeScenesAtSelectionEndTime,
            'id',
        )[0]; // there should be only one active scene active at start and end time both
        if (!activeScene) {
            throw new Error('Active scene not found');
        }
        const sceneGroupPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const sceneGroupBaseAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const sceneGroupStartTime = sceneGroup.startTime;

        const translateToTranscriptTime = (
            time: number,
            { includeTrailingCut } = { includeTrailingCut: true },
        ) => {
            return translateVideoTimeToTranscriptTime(
                time,
                sceneGroupPlaybackRegions,
                sceneGroupBaseAssetSourceDuration,
                sceneGroupStartTime,
                {
                    includeTrailingCut,
                },
            );
        };
        const sceneStartTime = translateToTranscriptTime(getVoicedrivenSceneStartTime(activeScene));
        const sceneEndTime = translateToTranscriptTime(getVoicedrivenSceneEndTime(activeScene), {
            includeTrailingCut: false,
        });

        const newOverlayRegions: [number, number][] = call(
            getExistingOverlayTimesOperation,
            sceneGroupId,
        )
            // filter out the overlay that is being removed
            .filter(([startTime, endTime]) => {
                return (
                    !equalWithinEpsilon(startTime, sceneStartTime) &&
                    !equalWithinEpsilon(endTime, sceneEndTime)
                );
            });

        // add in new overlay regions to the left and right of the selection
        if (!equalWithinEpsilon(sceneStartTime, selectionStartTime)) {
            newOverlayRegions.push([sceneStartTime, selectionStartTime]);
        }
        if (!equalWithinEpsilon(sceneEndTime, selectionEndTime)) {
            newOverlayRegions.push([selectionEndTime, sceneEndTime]);
        }

        // rebuild all the overlays
        call(applyOverlaysToSceneGroupOperation, newOverlayRegions, sceneGroup);
    },
);

export const generateClipPlansOperation = createLuminaryOperation(
    async (
        { luminary, getState },
        {
            user_goal,
            min_duration,
            max_duration,
            num_clips,
        }: {
            user_goal: string;
            min_duration: number | null;
            max_duration: number | null;
            num_clips: number;
        },
    ) => {
        const mainSceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(luminary);
        const mainSceneGroupId = mainSceneGroup.id;

        const editor = getEditorFromSceneGroupId(getState(), mainSceneGroupId);
        if (!editor) {
            throw new Error('No lexical editor found for scene group');
        }

        const transcript = getTranscriptWords(editor, { includeEmptyWords: true })
            .map((w) => w.word)
            .join('');

        let response;
        try {
            response = await ApiService.clipPlannerCreate({
                body: {
                    user_goal,
                    transcript,
                    min_duration,
                    max_duration,
                },
            });
        } catch (error) {
            if (isApiErrorWithProperty(error, 'error')) {
                throw new ClipCreatorError(error.body.error as string, {
                    toastMessage: error.body.error as string,
                    captureError: false,
                });
            }
            throw error;
        }

        const ws = WebSocketClient.getInstance();
        const result = await ws.waitForMessage<ClipPlansResponse>(
            response.data.task_id,
            TaskResultMessageTypeEnum.TASK_RESULT,
            'clip plan create',
        );
        const plans = result.plans;
        if (plans.length === 0) {
            throw new ClipCreatorError('No clip plans generated', {
                toastMessage:
                    'We could not generate any clips. Please try changing your goal or duration settings.',
            });
        }
        num_clips = Math.min(num_clips, plans.length);
        return result.plans.slice(0, num_clips);
    },
);

export const generateClipsOperation = createLuminaryOperation(
    async ({ luminary, call, dispatch, getState }, plans: ClipPlan[], templateId: string) => {
        const mainSceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(luminary);
        const mainSceneGroupId = mainSceneGroup.id;

        const editor = getEditorFromSceneGroupId(getState(), mainSceneGroupId);
        if (!editor) {
            throw new Error('No lexical editor found for scene group');
        }

        const transcriptWords = getTranscriptWords(editor);
        const transcriptLanguage = getOriginalTranscriptLanguage(getState(), mainSceneGroupId);

        const waveformStyleId = getWaveformStyleSetting(getState());
        const waveforms = getWaveformIconSourceData(getState(), waveformStyleId);

        for (const plan of plans) {
            let response;
            try {
                response = await ApiService.transcriptClipCreate({
                    body: {
                        clip_plan: plan,
                        transcript_words: transcriptWords,
                        language_code: transcriptLanguage,
                    },
                });
            } catch (error) {
                if (isApiErrorWithProperty(error, 'error')) {
                    throw new ClipCreatorError(error.body.error as string, {
                        toastMessage: error.body.error as string,
                        captureError: false,
                    });
                }
                throw error;
            }

            try {
                const ws = WebSocketClient.getInstance();
                const result = await ws.waitForMessage<TranscriptClipResponse>(
                    response.data.task_id,
                    TaskResultMessageTypeEnum.TASK_RESULT,
                    'generate clips',
                );
                const clipTimeRecommendations = result.clip;
                if (clipTimeRecommendations.length === 0) {
                    continue; // sometimes the backend could return an empty list. Just ignore this plan - there's no output.
                }

                const clipId = await call(performNewClipOperation, templateId);
                call(updateClipCaptionStyleOperation, clipId, getCaptionStyleSetting(getState()));
                if (
                    waveformStyleId !== WAVEFORM_STYLE_NONE ||
                    isVoiceoverSceneGroup(mainSceneGroup)
                ) {
                    await call(updateClipWaveformStyleOperation, clipId, waveforms);
                }
                await call(
                    updateClipAspectRatioOperation,
                    clipId,
                    getAspectRatioSetting(getState()),
                    true,
                );
                dispatch(setClipTitle({ clipId, title: plan.title }));
                dispatch(setClipPrompt({ clipId, prompt: plan.content_prompt }));

                const voiceDrivenAssetStartTime = getVoiceDrivenBaseAssetStartTime(mainSceneGroup);
                const voiceDrivenAssetEndTime = getVoiceDrivenBaseAssetEndTime(mainSceneGroup);
                const { transcriptStartTime, transcriptEndTime } = editor
                    .getEditorState()
                    .read(() => ({
                        transcriptStartTime:
                            $getFirstTranscriptWordShowing(voiceDrivenAssetStartTime).startTime,
                        transcriptEndTime:
                            $getLastTranscriptWordShowing(voiceDrivenAssetEndTime).endTime,
                    }));

                const overlays: [number, number][] = clipTimeRecommendations.map(
                    ({ startTime, endTime }) => [
                        startTime <= transcriptStartTime ? 0 : startTime,
                        endTime >= transcriptEndTime ? mainSceneGroup.duration : endTime,
                    ],
                );

                call(applyOverlaysToVoiceDrivenSceneGroupOperation, overlays);
                call(applyOverlaysAsPlaybackRegionsToSelectedClipOperation);
                call(updateHasCutsForSelectedClipOperation);
                dispatch(updateNavigation());
            } catch {
                // If one clip plan fails to generate, continue with the remaining plans
                continue;
            }
        }
    },
);

/**
 * Gets the last used template id in the following order:
 * 1. Selected clip template id
 * 2. Last created clip template id
 * 3. First template id from templates
 */
export const getLastUsedClipTemplateIdOperation = createLuminaryOperation(
    async ({ getState, dispatch }) => {
        // Get selected clip template id
        const selectedClipId = getSelectedClipId(getState());
        if (selectedClipId) {
            const selectedClip = getClipById(getState(), selectedClipId);
            return selectedClip.luminary.template.id;
        }

        // Get last created clip template id
        const clipIds = getAllClipIds(getState());
        if (clipIds.length > 0) {
            const lastClip = getClipById(getState(), clipIds[clipIds.length - 1]);
            return lastClip.luminary.template.id;
        }

        let templateIds = getTemplateIdsForClipCreator(getState());
        if (templateIds.size === 0) {
            await dispatch(loadTemplates());
            templateIds = getTemplateIdsForClipCreator(getState());
        }

        // the preferred template could be archived, so might not have come with the templates list request.
        // if that's the case, we can still access it by explicitly requesting it.
        if (!templateIds.includes(CLIP_CREATOR_PREFERRED_CLIP_TEMPLATE)) {
            try {
                await dispatch(loadTemplate(CLIP_CREATOR_PREFERRED_CLIP_TEMPLATE));
                await dispatch(loadSceneDesignsForTemplate(CLIP_CREATOR_PREFERRED_CLIP_TEMPLATE));
                templateIds = getTemplateIdsForClipCreator(getState());
            } catch (err) {
                ErrorReporter.capture(err);
                // ignore this - maybe the preferred template is wrong or something
            }
        }

        // prefer lumen5_video_template over other templates because
        // it has the blueprint that we can use to toggle on overlays
        if (templateIds.includes(CLIP_CREATOR_PREFERRED_CLIP_TEMPLATE)) {
            return CLIP_CREATOR_PREFERRED_CLIP_TEMPLATE;
        }

        // get first template id
        const lastUsedTemplateId = templateIds.first();
        if (!lastUsedTemplateId) {
            throw new Error('No templates available to create clip');
        }

        return lastUsedTemplateId;
    },
);

export const fetchTemplateBundleOperation = createLuminaryOperation(
    async ({ dispatch, getState }, templateId: string) => {
        await dispatch(loadTemplate(templateId));
        await dispatch(loadSceneDesignsForTemplate(templateId));
        const templateBundle = getTemplateBundle(getState(), templateId);
        return templateBundle;
    },
);

export const fetchClipCreatorTemplateBundleOperation = createLuminaryOperation(async ({ call }) => {
    const templateBundle = await call(fetchTemplateBundleOperation, CLIP_CREATOR_PREVIEW_TEMPLATE);
    return templateBundle;
});

export const fetchLastUsedTemplateBundleOperation = createLuminaryOperation(async ({ call }) => {
    const templateId = await call(getLastUsedClipTemplateIdOperation);
    const templateBundle = await call(fetchTemplateBundleOperation, templateId);
    return templateBundle;
});

export const navigateToEditorOperation = createLuminaryOperation(({ getState }) => {
    const clipIds = getAllClipIds(getState());
    const videoIds = clipIds
        .map((clipId) => getClipById(getState(), clipId).videoId)
        .filter(isNotUndefined);
    Navigation.goToLocation(Urls.APP + `?id=${videoIds.join(',')}`);
});

const performUpdateVideoRequestForClipIdOperation = createLuminaryOperation(
    async (
        {
            getState,
            dispatch,
            luminary, // should be the clip luminary
        },
        clipId: ClipIDType,
    ) => {
        const clip = getClipById(getState(), clipId);
        const videoId = clip.videoId;
        if (!videoId) {
            throw new Error('Video id not available');
        }

        const videoData = saveRawLuminary(luminary);
        const videoInitType = VIDEO_INIT_TYPE.TALKING_HEAD;
        const userOverrides = null;
        const allSessionUploadedAssetUuids = ImmutableToJS(
            getAllSessionUploadedAssetUuids(getState()),
        );
        const tabDurations = null;
        const templateId = luminary.template.id;
        const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(luminary);
        const videoTranscripts = dispatch(getTranscriptsToSaveForClipAction(sceneGroup.id));
        const brandKitId = null;
        const videoUpdatedByUser = true;
        const articleOriginalContent = null;
        const scriptComposerData = null;
        const render = false;
        const asyncGenerationId = null;
        const asyncGenerationData = null;
        const videoDataHash = 'HASH'; // not used because isForceSave is true
        const isForceSave = true; // so we can save without video hash

        await ApiService.videoUpdate({
            path: {
                video_id: videoId.toString(),
            },
            body: {
                video_data: videoData,
                user_overrides: userOverrides,
                transcripts: videoTranscripts,
                user_uploads: allSessionUploadedAssetUuids,
                template_id: templateId,
                aspect_ratio: luminary.aspectRatio,
                brand_kit_id: brandKitId,
                video_data_hash: videoDataHash,
                video_updated_by_user: videoUpdatedByUser,
                video_init_type: videoInitType,
                script_composer_data: scriptComposerData,
                article_original_content: articleOriginalContent,
                tab_durations: tabDurations,
                render: render,
                async_generation_id: asyncGenerationId,
                async_generation_data: asyncGenerationData,
                is_force_save: isForceSave,
            },
        });
    },
);

const performCreateVideoRequestForClipIdOperation = createLuminaryOperation(
    async (
        {
            getState,
            dispatch,
            luminary, // should be the clip luminary
        },
        clipId: ClipIDType,
    ) => {
        const projectId = getClipCreatorProjectId();

        await new Promise<void>((resolve, reject) => {
            if (isTemplateValid(luminary.template.id)) {
                resolve();
                return;
            }
            const timeout = setTimeout(() => {
                clearInterval(interval);
                reject(new Error('Timed out waiting for valid template ID'));
            }, 10000);
            const interval = setInterval(() => {
                if (isTemplateValid(luminary.template.id)) {
                    clearInterval(interval);
                    clearTimeout(timeout);
                    resolve();
                }
            }, 50);
        });

        const videoData = saveRawLuminary(luminary);
        const clip = getClipById(getState(), clipId);

        const response = await ApiService.videoCreate({
            body: {
                video_data: videoData,
                aspect_ratio: String(luminary.aspectRatio || VideoData.LANDSCAPE_ASPECT_RATIO),
                template: luminary.template.id,
                project_id: projectId,
                creation_source: CreationSourceEnum.CLIP_CREATOR,
                name: clip.title,
            },
        });
        const videoId = response.data.video_id;
        dispatch(setVideoIdForClipId({ clipId, videoId }));
    },
);

export const performSaveClipOperation = createLuminaryOperation(
    async (
        // call this operation with the clip luminary
        { call },
        clipId: ClipIDType,
    ) => {
        try {
            await call(performCreateVideoRequestForClipIdOperation, clipId);
            await call(performUpdateVideoRequestForClipIdOperation, clipId);
            return true;
        } catch (e) {
            if (isVideoLimitError(e)) {
                throw e;
            }
            ErrorReporter.capture(e);
            return false;
        }
    },
);

export const performOverwriteClipOperation = createLuminaryOperation(
    async (
        // call this operation with the clip luminary
        { call },
        clipId: ClipIDType,
    ) => {
        try {
            await call(performUpdateVideoRequestForClipIdOperation, clipId);
            return true;
        } catch (e) {
            ErrorReporter.capture(e);
            return false;
        }
    },
);

export const scrollToFirstOverlayInTranscriptOperation = createLuminaryOperation(
    ({ luminary, getState, call, dispatch }) => {
        const sceneGroup = getVoiceDrivenSceneGroupOrUndefinedFromLuminary(luminary);
        if (!sceneGroup) {
            return;
        }
        const firstOverlay = getOverlayScenesInSceneGroup(getState(), sceneGroup)[0];
        if (!firstOverlay) {
            return;
        }
        const overlayStartTime = getVoicedrivenSceneStartTime(firstOverlay);
        const playbackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const sourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const transcriptTime = translateVideoTimeToTranscriptTime(
            overlayStartTime,
            playbackRegions,
            sourceDuration,
            sceneGroup.startTime,
            { includeTrailingCut: true },
        );
        dispatch(
            setFocusedSceneInfo({
                sceneGroupId: sceneGroup.id,
                sceneId: firstOverlay.id,
                subsceneIndex: 0,
            }),
        );
        ClipCreatorEventEmitter.emit(CLIP_CREATOR_EVENTS.SEEK_PREVIEW, {
            seekTimeMillis: transcriptTime,
        });

        call(scrollToTimeOperation, sceneGroup.id, transcriptTime);
    },
);

export const scrollToTimeOperation = createLuminaryOperation(
    ({ getState }, sceneGroupId: string, time: number) => {
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            return;
        }

        const word = editor.getEditorState().read(() => {
            return $getNextWordAtTime(time);
        });
        if (!word) {
            return;
        }
        const wordElement = editor.getElementByKey(word.getKey());
        if (!wordElement) {
            return;
        }

        const container = FocusAndScroll.getScrollParent(wordElement);
        if (!container) {
            return;
        }

        const wordRect = wordElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const wordTop = wordRect.top - containerRect.top + container.scrollTop;

        /**
         * If word is already visible, do not scroll
         */
        if (container.scrollTop < wordTop && wordTop < container.scrollTop + containerRect.height) {
            return;
        }

        /**
         * Try to make the first overlay appear within the top 25% of container
         */
        const top = wordTop - containerRect.height / 4;
        container.scrollTo({
            top: Math.min(Math.max(0, top), container.scrollHeight),
            behavior: 'smooth',
        });
    },
);

const updateClipCaptionStyleOperation = createLuminaryOperation(
    ({ getState, call }, clipId: ClipIDType, captionStyle: string) => {
        const versionIds = getClipVersionIds(getState(), clipId);
        for (const versionId of versionIds) {
            const { luminary } = getClipVersionById(getState(), clipId, versionId);
            if (!luminary) {
                throw new Error(`Luminary for clip version with id ${versionId} not found`);
            }

            // if the user selects "None", we want to just hide the captions
            if (captionStyle === CAPTION_STYLE_NONE) {
                call(setAllSceneGroupsCaptionVisibilityOperation, false, {
                    luminary,
                });
                return;
            }

            // if the user selects a style, we want to show the captions
            call(setAllSceneGroupsCaptionVisibilityOperation, true, {
                luminary,
            });

            // and set the style
            call(setCaptionsSceneDesignOperation, captionStyle, {
                luminary,
            });
        }
    },
);

const getWaveformIconSourceData = (
    state: RootState,
    waveformId: string,
): IconSourceData | undefined => {
    const orm = getMediaResultsORM(state, SEARCH_CONTEXT.WAVEFORMS);
    const allIds = orm.getAllIds();
    let icon: ImmutableMap<IconAssetModel> | undefined;
    allIds.forEach((id) => {
        const item = orm.getById(id);
        const itemIcon = item.get('icon');
        if (itemIcon && itemIcon.get('uuid') === waveformId) {
            icon = itemIcon;
        }
    });
    if (!icon) {
        return;
    }
    return {
        url: icon.get('url'),
        uuid: icon.get('uuid') ?? '',
        source: icon.get('source'),
        source_id: icon.get('source_id') ?? '',
        license: String(icon.get('license')),
        animated: icon.get('animated') ?? false,
    } as IconSourceData;
};

const updateClipWaveformStyleOperation = createLuminaryOperation(
    async ({ getState, call }, clipId: ClipIDType, waveformData?: IconSourceData) => {
        if (!waveformData) {
            return;
        }

        for (const versionId of getClipVersionIds(getState(), clipId)) {
            const { luminary } = getClipVersionById(getState(), clipId, versionId);
            if (!luminary) continue;

            const templateId = luminary.template.id;
            const chosenSceneDesignId = chooseWaveformSceneDesign(getState(), templateId);
            if (!chosenSceneDesignId) {
                throw new ClipCreatorError('Could not find scene design to use for Clip Creator', {
                    toastMessage: 'Failed to generate clips, please try using a different template',
                });
            }

            const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(luminary);
            const sourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);

            const waveformSceneId = call(
                moveOtherOverlaysAndCreateOverlayOperation,
                sceneGroup.id,
                0,
                sourceDuration,
                chosenSceneDesignId,
                true,
                undefined,
                false,
                [],
                { luminary },
            );

            const waveformScene = luminary.getSceneById(waveformSceneId);
            if (!waveformScene) continue;

            const existingWaveformIcon = waveformScene
                .getAssets()
                .find(
                    (asset) =>
                        VideoData.isIconAsset(asset) &&
                        asset.playbackMode === VideoData.PLAYBACK_WAVEFORM,
                );

            const canvasWidth = luminary.referenceResolution.width;
            const canvasHeight = luminary.referenceResolution.height;
            const waveformWidth = canvasWidth * 0.4;
            const waveformHeight = canvasHeight * 0.2;

            const existingBounds = waveformScene
                .getAssets()
                .filter((asset) => !VideoData.isColorAsset(asset))
                .map((asset) => {
                    const nb = getNormalizedAssetBounds(asset);
                    return {
                        x: nb.normalizedX * canvasWidth,
                        y: nb.normalizedY * canvasHeight,
                        width: nb.normalizedWidth * canvasWidth,
                        height: nb.normalizedHeight * canvasHeight,
                    };
                });

            const targetPosition = existingWaveformIcon
                ? null
                : findNonOverlappingPosition(
                      existingBounds,
                      canvasWidth,
                      canvasHeight,
                      waveformWidth,
                      waveformHeight,
                  );

            call(
                setIconSouceDataForSceneOperation,
                waveformSceneId,
                waveformData,
                {
                    targetAssetId: existingWaveformIcon?.id ?? null,
                    targetPosition,
                    targetDimensions: existingWaveformIcon
                        ? null
                        : { width: waveformWidth, height: waveformHeight },
                    isWaveform: true,
                },
                true,
                undefined,
                { luminary },
            );
        }
    },
);

export const luminarySeekToTranscriptTimeOperation = createLuminaryOperation(
    async ({ luminary }, transcriptTime: number, sceneGroupId: string) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        const videoTime = translateTranscriptTimeToVideoTime(
            transcriptTime,
            getVoiceDrivenAssetPlaybackRegions(sceneGroup),
            getVoiceDrivenBaseAssetSourceDuration(sceneGroup),
            sceneGroup.startTime,
        );
        await luminary.seek(videoTime, VideoData.SeekMode.ResolveWhenCanPlay);
    },
);

export const handleGenerateContentPlannerClipsOperation = createLuminaryOperation(
    async (
        { call },
        {
            userGoal,
            minDuration,
            maxDuration,
            numClips,
            templateId,
        }: {
            userGoal: string;
            minDuration: number | null;
            maxDuration: number | null;
            numClips: number;
            templateId: string;
        },
    ) => {
        try {
            const goals = [
                `Generate ${numClips} ${pluralize(numClips, 'clip')} that will perform well on social media. Present them in chronological order.`,
            ];
            if (userGoal) {
                goals.push(userGoal);
            }
            const user_goal = goals.join('\n\n');
            const plans = await call(generateClipPlansOperation, {
                user_goal,
                min_duration: minDuration,
                max_duration: maxDuration,
                num_clips: numClips,
            });
            await call(generateClipsOperation, plans, templateId);
        } catch (error) {
            throw new ClipCreatorError(error, {
                toastMessage:
                    'Something went wrong while generating clips from your video. Please try again.',
                toastDuration: INFINITE_TOAST_DURATION,
            });
        }
    },
);

const applyDefaultDesignAndCropOperation = createLuminaryOperation(
    ({ getState, call }, clipId: ClipIDType, crop: ClipMediaCrop) => {
        const clip = getClipById(getState(), clipId);
        if (!clip) {
            return;
        }

        const clipLuminary = clip.luminary;
        const sceneGroup = getVoiceDrivenSceneGroupOrThrowFromLuminary(clipLuminary);

        let scene = sceneGroup.scenes[0];
        if (!scene) {
            return;
        }
        const sceneDesignId = scene.sceneDesign;
        const templateId = clipLuminary.template.id;
        const baseSceneDesignId = getTemplateDefaultTalkingHeadSceneDesign(getState(), templateId);
        if (!baseSceneDesignId) {
            throw new Error(
                `Default talking head scene design not found for template ${templateId}`,
            );
        }

        if (sceneDesignId !== baseSceneDesignId) {
            call(
                runVoicedrivenTranscriptBatchOperation,
                sceneGroup.id,
                () => {
                    const newScene = sceneGroup.addScene({
                        sceneDesignId: baseSceneDesignId,
                    });
                    setVoicedrivenSceneDuration(newScene, getVoicedrivenSceneDuration(scene));
                    sceneGroup.moveSceneToIndex(newScene.id, scene.localIndex);
                    sceneGroup.removeScene(scene.id);
                    scene = newScene;
                },
                { luminary: clipLuminary },
            );
        }

        const mediaAssets = scene.getAssets().filter(VideoData.isMediaAsset);
        if (mediaAssets.length === 0) {
            throw new Error('No media assets found in base talking head scene');
        }
        const mediaAsset = mediaAssets[0];
        const assetSourceWidth = mediaAsset.sourceData.width;
        const assetSourceHeight = mediaAsset.sourceData.height;
        const mediaTargetWidth = mediaAsset.mediaTargetResolution.width;
        const mediaTargetHeight = mediaAsset.mediaTargetResolution.height;

        const cropDimensions = {
            assetWidth: assetSourceWidth,
            assetHeight: assetSourceHeight,
            viewportWidth: mediaTargetWidth,
            viewportHeight: mediaTargetHeight,
        };

        let cropState: CropState;
        if (crop === ClipMediaCrop.FILL_SCENE) {
            cropState = createFillCropState(cropDimensions);
        } else if (crop === ClipMediaCrop.FIT_TO_SCENE) {
            cropState = createContainCropState(cropDimensions);
        } else {
            throw new Error(`Unsupported crop type: ${crop}`);
        }

        const zoomValue = Math.max(cropState.zoom, MEDIA_ZOOM.MIN_VALUE);
        const xTranslatePercent = cropState.xTranslatePercent;
        const yTranslatePercent = cropState.yTranslatePercent;
        const rotationValue = 0;
        const flipHorizontally = false;
        const flipVertically = false;

        setMediaCrop(
            clipLuminary,
            scene.id,
            mediaAsset.id,
            xTranslatePercent,
            yTranslatePercent,
            zoomValue,
            rotationValue,
            flipHorizontally,
            flipVertically,
        );
    },
);

const updateClipMediaCropOperation = createLuminaryOperation(
    async ({ getState, call }, clipId: ClipIDType, crop: ClipMediaCrop) => {
        const versionIds = getClipVersionIds(getState(), clipId);
        for (const versionId of versionIds) {
            const { luminary: clipLuminary } = getClipVersionById(getState(), clipId, versionId);
            if (!clipLuminary) {
                throw new Error(`Luminary for clip version with id ${versionId} not found`);
            }
            if (crop === ClipMediaCrop.SPLIT_SPEAKERS) {
                await call(applyFaceDetectionAndCroppingOperation, clipId, {
                    luminary: clipLuminary,
                });
            } else {
                call(applyDefaultDesignAndCropOperation, clipId, crop, { luminary: clipLuminary });
            }
        }
    },
);

export const handleChangeClipMediaCropOperation = createLuminaryOperation(
    async ({ call }, clipId: ClipIDType, crop: ClipMediaCrop) => {
        try {
            await call(updateClipMediaCropOperation, clipId, crop);
        } catch (error) {
            throw new ClipCreatorError(error, {
                toastMessage: 'We could not change the crop of the clip media. Please try again.',
            });
        }
    },
);
